import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  CustomCourse,
  ErrorType,
  MistakeRecord,
  Progress,
  SrsCard,
  StudyMode,
  User,
} from './types';
import { newCard, reviewCard, type Quality } from './srs';
import { sfx } from './audio';
import { AI_QUOTA_PER_DAY, MAX_MASTERY, type Grade, multiplier, xpFor } from './score';

const todayKey = (): string => new Date().toISOString().slice(0, 10);

interface ComboState {
  combo: number;          // current streak (any non-miss extends it)
  bestCombo: number;      // session best
  allTimeBest: number;    // persisted lifetime best
  lastGrade: Grade | null;
  burstSeq: number;       // increments each grade — components key off this
}

interface PromotionEvent {
  sentenceId: string;
  from: number;
  to: number;
  seq: number;
}

interface AppState {
  user: User;
  progress: Progress;
  mistakes: MistakeRecord[];
  srs: Record<string, SrsCard>;
  enrolledBookIds: string[];
  mastery: Record<string, number>;  // sentenceId -> mastery level (0,1,2,3)
  lastPromotion: PromotionEvent | null;
  customCourses: CustomCourse[];
  combo: ComboState;
  audioMuted: boolean;
  aiQuotaUsedToday: number;
  aiQuotaResetDate: string;

  // —— Actions ——
  setUser: (user: Partial<User>) => void;
  markSentenceComplete: (sentenceId: string) => void;
  bumpStreak: () => void;
  addMinutes: (n: number) => void;
  awardXp: (n: number) => void;

  recordMistake: (m: Omit<MistakeRecord, 'id' | 'timestamp' | 'reviewed'>) => void;
  reviewMistake: (id: string) => void;
  clearMistake: (id: string) => void;

  enrollVocabBook: (bookId: string, wordIds: string[]) => void;
  reviewWord: (wordId: string, q: Quality) => void;
  ensureCard: (wordId: string) => void;

  /** Submit a grade — updates combo, plays sfx, awards XP. Returns XP earned. */
  submitGrade: (g: Grade) => number;
  resetSessionCombo: () => void;
  toggleAudio: () => void;

  /** Sentence mastery — 0 new, 1 saw it, 2 typed it, 3 spoke it. Auto-promotes mode. */
  bumpMastery: (sentenceId: string) => void;

  /** Custom course CRUD. */
  addCustomCourse: (c: CustomCourse) => void;
  deleteCustomCourse: (id: string) => void;

  /** AI quota gate. */
  consumeAiQuota: () => boolean;
}

const DEFAULT_USER: User = {
  name: 'Yono',
  email: 'yono@speedrun.run',
  joinedAt: Date.now() - 12 * 24 * 60 * 60 * 1000,
  plan: 'free',
  avatarEmoji: '🦊',
};

const DEFAULT_PROGRESS: Progress = {
  completedSentenceIds: [],
  streakDays: 7,
  lastStudyDate: todayKey(),
  totalMinutes: 142,
  xp: 1280,
};

const DEFAULT_COMBO: ComboState = {
  combo: 0,
  bestCombo: 0,
  allTimeBest: 0,
  lastGrade: null,
  burstSeq: 0,
};

export const useAppStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: DEFAULT_USER,
      progress: DEFAULT_PROGRESS,
      mistakes: [],
      srs: {},
      enrolledBookIds: ['vb-1'],
      mastery: {},
      lastPromotion: null,
      customCourses: [],
      combo: DEFAULT_COMBO,
      audioMuted: false,
      aiQuotaUsedToday: 0,
      aiQuotaResetDate: todayKey(),

      setUser: (user) => set((s) => ({ user: { ...s.user, ...user } })),

      markSentenceComplete: (sentenceId) =>
        set((s) => {
          if (s.progress.completedSentenceIds.includes(sentenceId)) return s;
          return {
            progress: {
              ...s.progress,
              completedSentenceIds: [...s.progress.completedSentenceIds, sentenceId],
              xp: s.progress.xp + 10,
            },
          };
        }),

      bumpStreak: () =>
        set((s) => {
          const today = todayKey();
          if (s.progress.lastStudyDate === today) return s;
          return {
            progress: {
              ...s.progress,
              streakDays: s.progress.streakDays + 1,
              lastStudyDate: today,
            },
          };
        }),

      addMinutes: (n) =>
        set((s) => ({
          progress: { ...s.progress, totalMinutes: s.progress.totalMinutes + n },
        })),

      awardXp: (n) =>
        set((s) => ({
          progress: { ...s.progress, xp: s.progress.xp + n },
        })),

      recordMistake: (m) =>
        set((s) => ({
          mistakes: [
            {
              ...m,
              id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              timestamp: Date.now(),
              reviewed: false,
            },
            ...s.mistakes,
          ].slice(0, 200),
        })),

      reviewMistake: (id) =>
        set((s) => ({
          mistakes: s.mistakes.map((m) => (m.id === id ? { ...m, reviewed: true } : m)),
        })),

      clearMistake: (id) =>
        set((s) => ({ mistakes: s.mistakes.filter((m) => m.id !== id) })),

      enrollVocabBook: (bookId, wordIds) =>
        set((s) => {
          const srs = { ...s.srs };
          for (const wId of wordIds) {
            if (srs[wId] === undefined) srs[wId] = newCard(wId);
          }
          return {
            enrolledBookIds: s.enrolledBookIds.includes(bookId)
              ? s.enrolledBookIds
              : [...s.enrolledBookIds, bookId],
            srs,
          };
        }),

      ensureCard: (wordId) =>
        set((s) => {
          if (s.srs[wordId] !== undefined) return s;
          return { srs: { ...s.srs, [wordId]: newCard(wordId) } };
        }),

      reviewWord: (wordId, q) =>
        set((s) => {
          const card = s.srs[wordId] ?? newCard(wordId);
          return {
            srs: { ...s.srs, [wordId]: reviewCard(card, q) },
            progress: { ...s.progress, xp: s.progress.xp + (q === 0 ? 2 : 5) },
          };
        }),

      submitGrade: (g) => {
        const state = get();
        const wasComboLevel = multiplier(state.combo.combo);
        const newCombo = g === 'miss' ? 0 : state.combo.combo + 1;
        const newBest = Math.max(state.combo.bestCombo, newCombo);
        const newAllTime = Math.max(state.combo.allTimeBest, newCombo);
        const xp = xpFor(g, state.combo.combo);

        // Play sound effect.
        if (g === 'perfect') sfx.perfect();
        else if (g === 'great') sfx.great();
        else if (g === 'good') sfx.good();
        else { sfx.miss(); if (state.combo.combo >= 4) sfx.comboBreak(); }

        // Combo level-up sting.
        if (multiplier(newCombo) > wasComboLevel) sfx.comboUp();

        set({
          combo: {
            combo: newCombo,
            bestCombo: newBest,
            allTimeBest: newAllTime,
            lastGrade: g,
            burstSeq: state.combo.burstSeq + 1,
          },
          progress: { ...state.progress, xp: state.progress.xp + xp },
        });
        return xp;
      },

      resetSessionCombo: () =>
        set((s) => ({
          combo: { ...s.combo, combo: 0, bestCombo: 0, lastGrade: null },
        })),

      toggleAudio: () =>
        set((s) => {
          const next = !s.audioMuted;
          // Sync runtime mute flag with audio engine.
          void import('./audio').then((m) => m.setMuted(next));
          return { audioMuted: next };
        }),

      bumpMastery: (sentenceId) =>
        set((s) => {
          const from = s.mastery[sentenceId] ?? 0;
          const to = Math.min(MAX_MASTERY, from + 1);
          if (to === from) return s;
          const seq = (s.lastPromotion?.seq ?? 0) + 1;
          return {
            mastery: { ...s.mastery, [sentenceId]: to },
            lastPromotion: { sentenceId, from, to, seq },
          };
        }),

      addCustomCourse: (c) =>
        set((s) => ({ customCourses: [c, ...s.customCourses].slice(0, 50) })),

      deleteCustomCourse: (id) =>
        set((s) => ({ customCourses: s.customCourses.filter((c) => c.id !== id) })),

      consumeAiQuota: () => {
        const state = get();
        const today = todayKey();
        const used = state.aiQuotaResetDate === today ? state.aiQuotaUsedToday : 0;
        const cap = AI_QUOTA_PER_DAY[state.user.plan];
        if (used >= cap) return false;
        set({ aiQuotaUsedToday: used + 1, aiQuotaResetDate: today });
        return true;
      },
    }),
    {
      name: 'speedrun-app-state-v4',
      partialize: (s) => ({
        user: s.user,
        progress: s.progress,
        mistakes: s.mistakes,
        srs: s.srs,
        enrolledBookIds: s.enrolledBookIds,
        mastery: s.mastery,
        customCourses: s.customCourses,
        combo: { ...s.combo, combo: 0, bestCombo: 0, lastGrade: null }, // session resets
        audioMuted: s.audioMuted,
        aiQuotaUsedToday: s.aiQuotaUsedToday,
        aiQuotaResetDate: s.aiQuotaResetDate,
      }),
    },
  ),
);

export type { ErrorType, MistakeRecord, StudyMode };

/** Shuffle helper — used by puzzle mode to scramble token order. */
export function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Tokenize an English sentence into puzzle tiles (words + punctuation kept attached). */
export function tokenize(sentence: string): string[] {
  return sentence.split(/\s+/).filter((t) => t.length > 0);
}
